"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link, useNavigate, useLocation } from "react-router-dom"

import { Tabs, TabList, TabPanels, Tab, TabPanel } from "@chakra-ui/react"

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Textarea,
  SimpleGrid,
  Select,
  Image,
  Flex,
  Text,
  Box,
  FormErrorMessage,
  FormHelperText,
  Tooltip as ChakraTooltip,
  Badge,
  useDisclosure,
  Spinner,
  Progress,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from "@chakra-ui/react"

import {
  Pen,
  Building2,
  Camera,
  Mail,
  CheckCircle2,
  Plus,
  User,
  Globe,
  Phone,
  Users,
  TrendingUp,
  FileText,
  Factory,
  MapPin,
  BuildingIcon,
  Send,
  UserCheck,
  Calendar,
  UserMinusIcon,
  MessageCircleIcon,
  UserPlusIcon,
  PauseCircleIcon,
  Info,
  ChevronDown,
  ChevronUp,
  X,
  ShieldCheck,
  ExternalLink,
  Link2,
  AlertTriangle,
  UploadCloud,
} from "lucide-react"

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts"

import {
  fetchProfileAsync,
  selectCompanyProfile,
  updateProfileAsync,
} from "../employerDashAPIs/companyProfile/CompanyProfileSlice"
import "./CompanyProfilePage.css"
import { addEmployerLogoAsync } from "../employerDashboardSlice/EmployerLogo/EmployerLogoSlice"
import CompanyAbout from "./CompanyAbout"
import EmployerMedia from "../employerMedia/EmployerMedia"
import {
  fetchCitiesAsync,
  fetchCountryAsync,
  fetchStateAsync,
  selectFetchCities,
  selectFetchCountry,
  selectFetchState,
} from "../AboutYou/AboutSlice"

import { fetchEmployerActiveSubscriptionAsync } from "../employerDashboardSlice/EmployerActiveSubscriptions/EmployerActiveSubscriptionSlice"
import { CompanyProfileSkeleton } from "./CompanyProfileSkeleton"
import { PhoneArrowDownLeftIcon } from "@heroicons/react/24/outline"

// ----------------------------------------------------------
// Completeness logic config (Weighted)
// ----------------------------------------------------------
const MIN_COMPLETENESS_TO_SKIP_MODAL = 0.6 // don't auto-open if >= 60% complete
const PROFILE_MODAL_SESSION_KEY = "profileModal:shownInSession"

// Field config (weights decide how much % each field contributes)
const FIELD_CONFIG = [
  // Required (heavier weights)
  { id: "company_name", path: "company_name", label: "Company Name", weight: 10, required: true, group: "Basics" },
  { id: "contact_person_name", path: "contact_person_name", label: "Contact Person", weight: 8, required: true, group: "Basics" },
  { id: "mobile", path: "mobile", label: "Mobile", weight: 8, required: true, group: "Basics" },
  { id: "address1", path: "address1", label: "Address Line 1", weight: 6, required: true, group: "Address" },
  { id: "pincode", path: "pincode", label: "Pincode", weight: 6, required: true, group: "Address" },
  { id: "country", path: ["country", "uuid"], label: "Country", weight: 6, required: true, group: "Address" },
  { id: "state", path: ["state", "uuid"], label: "State", weight: 6, required: true, group: "Address" },
  { id: "city", path: ["city", "uuid"], label: "City", weight: 6, required: true, group: "Address" },

  // Recommended (lighter weights)
  { id: "company_description", path: "company_description", label: "Company Description", weight: 6, group: "About" },
  { id: "industry_type", path: "industry_type", label: "Industry Type", weight: 4, group: "About" },
  { id: "company_type", path: "company_type", label: "Company Type", weight: 4, group: "About" },
  { id: "url", path: "url", label: "Website URL", weight: 4, group: "About" },
  { id: "number_of_employee", path: "number_of_employee", label: "Number of Employees", weight: 3, group: "About" },
  { id: "pan_number", path: "pan_number", label: "PAN Number", weight: 5, group: "Compliance" },
  { id: "gst_number", path: "gst_number", label: "GST Number", weight: 5, group: "Compliance" },
  { id: "tan_number", path: "tan_number", label: "TAN Number", weight: 3, group: "Compliance" },
  { id: "name_as_pan_number", path: "name_as_pan_number", label: "Name on PAN", weight: 2, group: "Compliance" },
  { id: "phone", path: "phone", label: "Phone", weight: 2, group: "Contact" },
  { id: "company_logo", path: "company_logo", label: "Company Logo", weight: 6, group: "Branding" }, // counts logo too
]

const FIELD_MAP = FIELD_CONFIG.reduce((acc, f) => {
  acc[f.id] = f
  return acc
}, {})

const getVal = (obj, pathOrKey) => {
  if (Array.isArray(pathOrKey)) return pathOrKey.reduce((acc, k) => acc?.[k], obj)
  return obj?.[pathOrKey]
}
const isNonEmpty = (v) => {
  if (v === null || v === undefined) return false
  if (typeof v === "string") return v.trim().length > 0
  return true
}

const computeProfileCompleteness = (profile) => {
  if (!profile) {
    return {
      percent: 0,
      missing: [],
      filled: [],
      stats: { requiredFilled: 0, requiredTotal: 0, recommendedFilled: 0, recommendedTotal: 0 },
      weights: { totalWeight: 0, filledWeight: 0 },
    }
  }

  const totalWeight = FIELD_CONFIG.reduce((sum, f) => sum + f.weight, 0)
  let filledWeight = 0
  const missing = []
  const filled = []

  let requiredFilled = 0
  const requiredTotal = FIELD_CONFIG.filter((f) => f.required).length
  let recommendedFilled = 0
  const recommendedTotal = FIELD_CONFIG.length - requiredTotal

  FIELD_CONFIG.forEach((f) => {
    const value = getVal(profile, f.path)
    if (isNonEmpty(value)) {
      filled.push({ id: f.id, label: f.label, weight: f.weight, group: f.group, required: !!f.required })
      filledWeight += f.weight
      if (f.required) requiredFilled += 1
      else recommendedFilled += 1
    } else {
      missing.push({
        id: f.id,
        label: f.label,
        weight: f.weight,
        group: f.group,
        required: !!f.required,
        contributionPercent: 0,
      })
    }
  })

  missing.forEach((m) => {
    m.contributionPercent = totalWeight ? Math.round((m.weight / totalWeight) * 100) : 0
  })

  missing.sort((a, b) => b.weight - a.weight)

  const percent = totalWeight ? filledWeight / totalWeight : 1

  return {
    percent,
    missing,
    filled,
    stats: { requiredFilled, requiredTotal, recommendedFilled, recommendedTotal },
    weights: { totalWeight, filledWeight },
  }
}

// ----------------------------------------------------------

const iconColors = {
  profile: "text-zinc-600",
  email: "text-zinc-600",
  phone: "text-zinc-600",
  companyType: "text-zinc-600",
  industryType: "text-zinc-600",
  contactPerson: "text-zinc-600",
  website: "text-zinc-600",
  tanNumber: "text-zinc-600",
  gstNumber: "text-zinc-600",
  panNumber: "text-zinc-600",
  nameOnPAN: "text-zinc-600",
  address: "text-zinc-600",
}

const colors = {
  adminbg: "#EFF3F6",
  LgColor: "#39465C",
  textBlue: "#174EA6",
  fontBlue: "#1A73E8",
  lightGray: "#F3F4F6",
  darkGray: "#4B5563",
}

function EditModal({ isOpen, onClose, profileData, onUpdate }) {
  const [formData, setFormData] = useState(profileData)
  const [isLoading, setIsLoading] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const toast = useToast()
  const dispatch = useDispatch()
  const countryList = useSelector(selectFetchCountry)
  const stateList = useSelector(selectFetchState)
  const citiesList = useSelector(selectFetchCities)
  const [selectedCountryId, setSelectedCountryId] = useState(profileData.country?.uuid || "")
  const [selectedStateId, setSelectedStateId] = useState(profileData.state?.uuid || "")

  const [gstError, setGstError] = useState(false)
  const {
    isOpen: confirmOpen,
    onOpen: openConfirm,
    onClose: closeConfirm,
  } = useDisclosure()
  const cancelRef = useRef()

  useEffect(() => {
    if (countryList.length === 0) {
      dispatch(fetchCountryAsync())
    }
  }, [countryList.length, dispatch])

  useEffect(() => {
    if (selectedCountryId) {
      dispatch(fetchStateAsync(selectedCountryId))
    }
  }, [selectedCountryId, dispatch])

  useEffect(() => {
    if (selectedStateId) {
      dispatch(fetchCitiesAsync(selectedStateId))
    }
  }, [selectedStateId, dispatch])

  useEffect(() => {
    setFormData(profileData)
    setSelectedCountryId(profileData.country?.uuid || "")
    setSelectedStateId(profileData.state?.uuid || "")
    validateGST(profileData.gst_number || "")
    setIsDirty(false)
  }, [profileData, isOpen])

  const normalizeUpper = (val) => (typeof val === "string" ? val.toUpperCase() : val)
  const onlyDigits = (val, maxLen) => {
    const clean = (val || "").replace(/\D/g, "")
    return maxLen ? clean.slice(0, maxLen) : clean
  }

  const handleChange = (e) => {
    const { name } = e.target
    let { value } = e.target

    // Soft transforms for better UX
    if (["gst_number", "pan_number", "tan_number"].includes(name)) {
      value = normalizeUpper(value)
    }
    if (name === "mobile") {
      value = onlyDigits(value, 10)
    }
    if (name === "pincode") {
      value = onlyDigits(value, 6)
    }

    if (name === "gst_number") {
      validateGST(value)
    }

    setFormData((prev) => {
      const next = { ...prev, [name]: value }
      if (!isDirty) setIsDirty(true)
      return next
    })
  }

  const validateGST = (gstNumber) => {
    if (!gstNumber) {
      setGstError(false)
      return true
    }
    const gstPattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/
    const isValid = gstPattern.test(gstNumber)
    setGstError(!isValid)
    return isValid
  }

  const handleClose = () => {
    if (isLoading) return
    if (isDirty) {
      openConfirm()
    } else {
      onClose()
    }
  }

  const confirmDiscard = () => {
    closeConfirm()
    onClose()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateGST(formData.gst_number)) {
      toast({
        title: "Invalid GST Number",
        description: "Please enter a valid GST number or leave it blank",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
      return
    }
    setIsLoading(true)
    const updatedData = {
      ...formData,
      country_uuid: formData.country?.uuid || selectedCountryId,
      state_uuid: formData.state?.uuid || selectedStateId,
      city_uuid: formData.city?.uuid || "",
    }
    try {
      await onUpdate(updatedData)
      toast({
        title: "Profile updated",
        description: "Your company profile was updated successfully",
        status: "success",
        duration: 2500,
        isClosable: true,
      })
      setIsDirty(false)
      onClose()
    } catch (error) {
      toast({
        title: "Update failed",
        description: "Failed to update profile. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const isFormValid =
    formData.company_name &&
    formData.contact_person_name &&
    selectedCountryId &&
    selectedStateId &&
    formData.city?.uuid &&
    formData.email &&
    /^\S+@\S+\.\S+$/.test(formData.email) &&
    formData.mobile &&
    /^[0-9]{10}$/.test(formData.mobile) &&
    formData.address1 &&
    formData.pincode &&
    /^[0-9]{6}$/.test(formData.pincode) &&
    !gstError

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} isCentered size="5xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader bg="blue.500" color="white" borderTopRadius="md" display="flex" alignItems="center" gap={2}>
            <Pen size={18} /> Edit Company Profile
            <Box ml="auto">
              <Badge colorScheme="yellow" variant="subtle">
                Changes {isDirty ? "not saved" : "saved"}
              </Badge>
            </Box>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <form onSubmit={handleSubmit}>
            <ModalBody py={6}>
              <VStack spacing={6}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} width="100%">
                  <FormControl isRequired isInvalid={!formData.company_name}>
                    <FormLabel>Company Name</FormLabel>
                    <Input
                      name="company_name"
                      value={formData.company_name || ""}
                      onChange={handleChange}
                      placeholder="Enter company name"
                    />
                    {!formData.company_name && <FormErrorMessage>Company name is required</FormErrorMessage>}
                  </FormControl>

                  <FormControl isRequired isInvalid={!formData.contact_person_name}>
                    <FormLabel>Contact Person Name</FormLabel>
                    <Input
                      name="contact_person_name"
                      value={formData.contact_person_name || ""}
                      onChange={handleChange}
                      placeholder="Enter contact person name"
                    />
                    {!formData.contact_person_name && (
                      <FormErrorMessage>Contact person name is required</FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl>
                    <FormLabel>Role</FormLabel>
                    <Input name="role" value={formData.role || ""} onChange={handleChange} placeholder="Enter role" />
                  </FormControl>

                  <FormControl isRequired isInvalid={!selectedCountryId}>
                    <FormLabel>
                      Country{" "}
                      <ChakraTooltip label="Select your company country" hasArrow>
                        <Info size={14} style={{ display: "inline", marginLeft: 4 }} />
                      </ChakraTooltip>
                    </FormLabel>
                    <Select
                      name="country"
                      value={selectedCountryId}
                      onChange={(e) => {
                        setSelectedCountryId(e.target.value)
                        setFormData((prev) => ({
                          ...prev,
                          country: { uuid: e.target.value },
                          state: { uuid: "" },
                          city: { uuid: "" },
                        }))
                        setIsDirty(true)
                      }}
                      placeholder="Select a country"
                    >
                      {countryList.map((item) => (
                        <option key={item.id} value={item.uuid}>
                          {item.name}
                        </option>
                      ))}
                    </Select>
                    {!selectedCountryId && <FormErrorMessage>Country is required</FormErrorMessage>}
                  </FormControl>

                  <FormControl isRequired isInvalid={!selectedStateId}>
                    <FormLabel>State</FormLabel>
                    <Select
                      name="state"
                      value={selectedStateId}
                      onChange={(e) => {
                        setSelectedStateId(e.target.value)
                        setFormData((prev) => ({
                          ...prev,
                          state: { uuid: e.target.value },
                          city: { uuid: "" },
                        }))
                        setIsDirty(true)
                      }}
                      placeholder="Select a state"
                      isDisabled={!selectedCountryId}
                    >
                      {stateList.map((state) => (
                        <option key={state.id} value={state.uuid}>
                          {state.name}
                        </option>
                      ))}
                    </Select>
                    {!selectedStateId && <FormErrorMessage>State is required</FormErrorMessage>}
                  </FormControl>

                  <FormControl isRequired isInvalid={!formData.city?.uuid}>
                    <FormLabel>City</FormLabel>
                    <Select
                      name="city"
                      value={formData.city?.uuid || ""}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          city: { uuid: e.target.value },
                        }))
                        setIsDirty(true)
                      }}
                      placeholder="Select a city"
                      isDisabled={!selectedStateId}
                    >
                      {Array.isArray(citiesList) &&
                        citiesList.map((city) => (
                          <option key={city.id} value={city.uuid}>
                            {city.name}
                          </option>
                        ))}
                    </Select>
                    {!formData.city?.uuid && <FormErrorMessage>City is required</FormErrorMessage>}
                  </FormControl>

                  <FormControl isRequired isInvalid={!formData.email || !/^\S+@\S+\.\S+$/.test(formData.email)}>
                    <FormLabel>
                      Email{" "}
                      <Badge ml={2} variant="subtle" colorScheme="gray">
                        Read-only
                      </Badge>
                    </FormLabel>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email || ""}
                      onChange={handleChange}
                      placeholder="Enter email address"
                      disabled
                    />
                    {!formData.email ? (
                      <FormErrorMessage>Email is required</FormErrorMessage>
                    ) : (
                      !/^\S+@\S+\.\S+$/.test(formData.email) && (
                        <FormErrorMessage>Please enter a valid email</FormErrorMessage>
                      )
                    )}
                  </FormControl>

                  <FormControl isRequired isInvalid={!formData.mobile || !/^[0-9]{10}$/.test(formData.mobile)}>
                    <FormLabel>Mobile</FormLabel>
                    <Input
                      name="mobile"
                      value={formData.mobile || ""}
                      onChange={handleChange}
                      placeholder="Enter mobile number"
                      maxLength={10}
                    />
                    {!formData.mobile ? (
                      <FormErrorMessage>Mobile is required</FormErrorMessage>
                    ) : (
                      !/^[0-9]{10}$/.test(formData.mobile) && (
                        <FormErrorMessage>Please enter a valid 10-digit mobile number</FormErrorMessage>
                      )
                    )}
                  </FormControl>

                  <FormControl>
                    <FormLabel>
                      Phone{" "}
                      <Badge ml={2} variant="subtle" colorScheme="gray">
                        Read-only
                      </Badge>
                    </FormLabel>
                    <Input
                      name="phone"
                      value={formData.phone || ""}
                      onChange={handleChange}
                      placeholder="Enter phone number"
                      disabled
                    />
                  </FormControl>

                  <FormControl
                    isInvalid={
                      formData.url && !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(formData.url)
                    }
                  >
                    <FormLabel>
                      Website URL{" "}
                      <ChakraTooltip label="Include https:// for clickable link" hasArrow>
                        <Info size={14} style={{ display: "inline", marginLeft: 4 }} />
                      </ChakraTooltip>
                    </FormLabel>
                    <Input
                      name="url"
                      value={formData.url || ""}
                      onChange={handleChange}
                      placeholder="https://example.com"
                    />
                    {formData.url && !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(formData.url) && (
                      <FormErrorMessage>Please enter a valid URL</FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl gridColumn={{ base: "span 1", md: "span 2" }}>
                    <FormLabel>Company Description</FormLabel>
                    <Textarea
                      name="company_description"
                      value={formData.company_description || ""}
                      onChange={handleChange}
                      placeholder="What do you do? Mission, product/service, culture..."
                      rows={3}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Industry Type</FormLabel>
                    <Input
                      name="industry_type"
                      value={formData.industry_type || ""}
                      onChange={handleChange}
                      placeholder="e.g., IT Services, Manufacturing"
                    />
                  </FormControl>

                  <FormControl isInvalid={gstError}>
                    <FormLabel>GST Number</FormLabel>
                    <Input
                      name="gst_number"
                      value={formData.gst_number || ""}
                      onChange={handleChange}
                      placeholder="Enter GST number"
                    />
                    {gstError && (
                      <FormErrorMessage>
                        Please enter a valid GST number (2 digits, 5 letters, 4 digits, 1 letter, 1 alphanumeric, Z, 1
                        alphanumeric)
                      </FormErrorMessage>
                    )}
                    <FormHelperText>Format: 27AAPFU0939F1ZV</FormHelperText>
                  </FormControl>

                  <FormControl isInvalid={formData.tan_number && !/^[A-Z]{4}[0-9]{5}[A-Z]{1}$/.test(formData.tan_number)}>
                    <FormLabel>TAN Number</FormLabel>
                    <Input
                      name="tan_number"
                      value={formData.tan_number || ""}
                      onChange={handleChange}
                      placeholder="Enter TAN number"
                    />
                    {formData.tan_number && !/^[A-Z]{4}[0-9]{5}[A-Z]{1}$/.test(formData.tan_number) && (
                      <FormErrorMessage>Please enter a valid TAN number</FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl isInvalid={formData.pan_number && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan_number)}>
                    <FormLabel>PAN Number</FormLabel>
                    <Input
                      name="pan_number"
                      value={formData.pan_number || ""}
                      onChange={handleChange}
                      placeholder="Enter PAN number"
                    />
                    {formData.pan_number && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan_number) && (
                      <FormErrorMessage>Please enter a valid PAN number</FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl>
                    <FormLabel>Name as per PAN</FormLabel>
                    <Input
                      name="name_as_pan_number"
                      value={formData.name_as_pan_number || ""}
                      onChange={handleChange}
                      placeholder="Enter name as per PAN"
                    />
                  </FormControl>

                  <FormControl isRequired isInvalid={!formData.address1}>
                    <FormLabel>Address Line 1</FormLabel>
                    <Input
                      name="address1"
                      value={formData.address1 || ""}
                      onChange={handleChange}
                      placeholder="Enter address line 1"
                    />
                    {!formData.address1 && <FormErrorMessage>Address line 1 is required</FormErrorMessage>}
                  </FormControl>

                  <FormControl>
                    <FormLabel>Address Line 2</FormLabel>
                    <Input
                      name="address2"
                      value={formData.address2 || ""}
                      onChange={handleChange}
                      placeholder="Enter address line 2"
                    />
                  </FormControl>

                  <FormControl isRequired isInvalid={!formData.pincode || !/^[0-9]{6}$/.test(formData.pincode)}>
                    <FormLabel>Pincode</FormLabel>
                    <Input
                      name="pincode"
                      value={formData.pincode || ""}
                      onChange={handleChange}
                      placeholder="Enter pincode"
                      maxLength={6}
                    />
                    {!formData.pincode ? (
                      <FormErrorMessage>Pincode is required</FormErrorMessage>
                    ) : (
                      !/^[0-9]{6}$/.test(formData.pincode) && (
                        <FormErrorMessage>Please enter a valid 6-digit pincode</FormErrorMessage>
                      )
                    )}
                  </FormControl>

                  <FormControl>
                    <FormLabel>Number of Employees</FormLabel>
                    <Input
                      name="number_of_employee"
                      value={formData?.number_of_employee || ""}
                      onChange={handleChange}
                      placeholder="e.g., 50-100"
                      type="text"
                    />
                    <FormHelperText>Enter a range or exact number</FormHelperText>
                  </FormControl>
                </SimpleGrid>
              </VStack>
            </ModalBody>
            <ModalFooter bg="gray.50">
              <Button
                colorScheme="blue"
                mr={3}
                type="submit"
                isLoading={isLoading}
                loadingText="Updating..."
                isDisabled={!isFormValid || isLoading}
                leftIcon={<ShieldCheck size={16} />}
              >
                Update Profile
              </Button>
              <Button onClick={handleClose} isDisabled={isLoading} variant="ghost">
                Cancel
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Confirm discard changes */}
      <AlertDialog isOpen={confirmOpen} leastDestructiveRef={cancelRef} onClose={closeConfirm} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" display="flex" alignItems="center" gap={2}>
              <AlertTriangle size={18} /> Discard changes?
            </AlertDialogHeader>
            <AlertDialogBody>
              You have unsaved changes. Are you sure you want to close without saving?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeConfirm}>
                Keep Editing
              </Button>
              <Button colorScheme="red" onClick={confirmDiscard} ml={3}>
                Discard
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  )
}

export default function CompanyProfile() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()

  const profileData = useSelector(selectCompanyProfile)
  const base_url = "https://api.indiahirings.com/uploads/company/"
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false)
  const [loading, setLoading] = useState(true)

  const [profilePercent, setProfilePercent] = useState(0)
  const [bannerExpanded, setBannerExpanded] = useState(false)
  const [bannerHidden, setBannerHidden] = useState(false) // Removed localStorage dependency

  const completeness = useMemo(() => computeProfileCompleteness(profileData), [profileData])

  const activeSub = profileData?.active_subscription
  const isDisabledjob = !activeSub || activeSub.remaining_job_post === 0 || activeSub === null
  const disabledReason = useMemo(() => {
    if (!activeSub) return "No active subscription"
    if (activeSub.remaining_job_post === 0) return "All job posts used"
    return ""
  }, [activeSub])

  // Fetch profile + subscription
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        await Promise.all([
          dispatch(fetchProfileAsync()).unwrap(),
          dispatch(fetchEmployerActiveSubscriptionAsync()).unwrap(),
        ])
      } catch (error) {
        console.error("Failed to fetch initial profile or subscription data:", error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [dispatch])

  // Redirect if record missing
  useEffect(() => {
    if (profileData && profileData.msg === "Record not found") {
      navigate("/employer-basics")
    }
  }, [profileData, navigate])

  // Compute completeness and auto-open modal if needed
  useEffect(() => {
    if (!loading && profileData && profileData.msg !== "Record not found") {
      const { percent } = computeProfileCompleteness(profileData)
      setProfilePercent(percent)

      const cameFromOnboarding = location.state?.openCompleteProfile === true
      const shownThisSession = sessionStorage.getItem(PROFILE_MODAL_SESSION_KEY) === "1"

      if (
        (cameFromOnboarding && percent < MIN_COMPLETENESS_TO_SKIP_MODAL) ||
        (!cameFromOnboarding && percent < MIN_COMPLETENESS_TO_SKIP_MODAL && !shownThisSession)
      ) {
        setIsEditModalOpen(true)
        sessionStorage.setItem(PROFILE_MODAL_SESSION_KEY, "1")
      }
    }
  }, [loading, profileData, location.state])

  const [selectedFile, setSelectedFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [logoSaving, setLogoSaving] = useState(false)

  const handleFileChange = (file) => {
    if (!file) return
    const isImage = file.type.startsWith("image/")
    const isLt2M = file.size / 1024 / 1024 < 2
    if (!isImage) {
      toast({ title: "Invalid file", description: "Please upload an image file", status: "error", duration: 3000 })
      return
    }
    if (!isLt2M) {
      toast({ title: "File too large", description: "Max size is 2MB", status: "error", duration: 3000 })
      return
    }
    setSelectedFile(file)
    setPreviewUrl(URL.createObjectURL(file))
  }

  const onDropLogo = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const file = e.dataTransfer.files?.[0]
    if (file) handleFileChange(file)
  }

  const saveLogo = async () => {
    if (!selectedFile) {
      toast({ title: "No file selected", description: "Please choose a logo to upload", status: "warning", duration: 2500 })
      return
    }
    setLogoSaving(true)
    const formData = new FormData()
    formData.append("company_logo", selectedFile)
    try {
      await dispatch(addEmployerLogoAsync({ formData }))
      await dispatch(fetchProfileAsync())
      setIsLogoModalOpen(false)
      toast({ title: "Logo updated", status: "success", duration: 2000 })
    } catch (error) {
      console.error("Error saving logo:", error)
      toast({ title: "Upload failed", description: "Please try again later", status: "error", duration: 3000 })
    } finally {
      setLogoSaving(false)
    }
  }

  const handleUpdateProfile = async (updatedData) => {
    try {
      await dispatch(updateProfileAsync({ uuid: updatedData.uuid, data: updatedData }))
      await dispatch(fetchProfileAsync())
      setIsEditModalOpen(false)
    } catch (error) {
      console.error("Failed to update profile:", error)
      throw error
    }
  }

  // Banner computed vars
  const percentDisplay = Math.round(profilePercent * 100)
  const topMissingCompact = completeness.missing.slice(0, 2)
  const topMissingExpanded = completeness.missing
  const sumTop3 = completeness.missing.slice(0, 3).reduce((acc, m) => acc + m.contributionPercent, 0)

  const handleHideBanner = () => {
    setBannerHidden(true)
  }

  const formatNumber = (n) => {
    const num = Number(n || 0)
    return new Intl.NumberFormat("en-IN").format(num)
  }

  const overviewData = useMemo(
    () => [
      { name: "Total Jobs", value: profileData?.total_job_count || 0 },
      { name: "Published", value: profileData?.total_published_job_count || 0 },
      {
        name: "Unpublished",
        value: (profileData?.total_job_count || 0) - (profileData?.total_published_job_count || 0),
      },
      { name: "Applicants", value: profileData?.total_applicant_count || 0 },
    ],
    [profileData]
  )

  const statusData = useMemo(
    () => [
      { name: "Applied", value: profileData?.status_counts?.applied || 0, color: colors.fontBlue },
      { name: "Shortlisted", value: profileData?.status_counts?.shortlist || 0, color: "#10B981" },
      { name: "Interviews", value: profileData?.status_counts?.interview_scheduled || 0, color: "#F59E0B" },
      { name: "Rejected", value: profileData?.status_counts?.rejected || 0, color: "#f52e0b" },
      { name: "On Hold", value: profileData?.status_counts?.on_hold || 0, color: "#f5c518" },
      { name: "Hired", value: profileData?.status_counts?.hired || 0, color: "#a3d9a5" },
      { name: "Contacted", value: profileData?.status_counts?.contacted || 0, color: "#5bc0de" },
    ],
    [profileData]
  )

  return (
    <div className="">
      <div className="p-3 bg-mainBg" style={{ fontFamily: "Inter, sans-serif" }}>
        <div className="sm:px-4 px-2 min-h-screen">
          {loading ? (
            <div className="">
              <CompanyProfileSkeleton />
            </div>
          ) : profileData ? (
            <div className="rounded-lg">
              {/* Compact Tailwind Banner (hidden when 100% or user closed it) */}
              {percentDisplay < 100 && !bannerHidden && (
                <div className="relative overflow-hidden rounded-lg border border-blue-100 bg-gradient-to-r from-blue-50 to-cyan-50 shadow-sm mb-3">
                  <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-blue-100 opacity-30" />
                  <div className="pointer-events-none absolute -left-24 -bottom-24 h-56 w-56 rounded-full bg-cyan-100 opacity-20" />

                  <div className="relative z-10 p-3">
                    {/* Header row */}
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-start gap-2 flex-1">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white/80 ring-1 ring-blue-100 shrink-0 mt-0.5">
                          <Info className="h-3.5 w-3.5 text-blue-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800">Complete your company profile</p>
                          <p className="text-xs text-slate-600">
                            <span className="font-semibold text-blue-700">{percentDisplay}% complete</span>. Fill key fields to reach 100%.
                          </p>
                        </div>
                      </div>

                      <button
                        onClick={handleHideBanner}
                        className="inline-flex h-6 w-6 items-center justify-center rounded text-slate-500 hover:bg-white hover:text-slate-700 shrink-0"
                        aria-label="Hide banner"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    {/* Action buttons and missing fields */}
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {topMissingCompact.map((m) => (
                          <button
                            key={m.id}
                            onClick={() => setIsEditModalOpen(true)}
                            className={`group inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs ring-1 transition ${m.required
                              ? "bg-red-50 text-red-700 ring-red-200 hover:bg-red-100"
                              : "bg-blue-50 text-blue-700 ring-blue-200 hover:bg-blue-100"
                              }`}
                          >
                            <span className="font-medium">{m.label}</span>
                            <span className="text-[10px] text-slate-500 group-hover:text-slate-700">
                              +{m.contributionPercent}%
                            </span>
                          </button>
                        ))}
                        {topMissingCompact.length === 0 && (
                          <span className="text-xs text-emerald-700">All complete!</span>
                        )}
                      </div>

                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setIsEditModalOpen(true)}
                          className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white shadow-sm hover:bg-blue-700"
                        >
                          <Plus className="h-3 w-3" />
                          Complete
                        </button>
                        <button
                          onClick={() => setBannerExpanded((s) => !s)}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-blue-700 hover:bg-white"
                        >
                          {bannerExpanded ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mb-2">
                      <div className="h-2 w-full rounded-full bg-white/70 overflow-hidden">
                        <div
                          className="h-2 rounded-full bg-blue-500 transition-all"
                          style={{ width: `${percentDisplay}%` }}
                        />
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <span className="text-[11px] font-medium text-emerald-700">
                          Required {completeness.stats.requiredFilled}/{completeness.stats.requiredTotal}
                        </span>
                        <span className="text-[10px] text-slate-400">•</span>
                        <span className="text-[11px] font-medium text-cyan-700">
                          Rec {completeness.stats.recommendedFilled}/{completeness.stats.recommendedTotal}
                        </span>
                      </div>
                    </div>

                    {/* Expanded details */}
                    {bannerExpanded && (
                      <div className="rounded border border-blue-100 bg-white/70 p-2">
                        <div className="mb-1.5 flex flex-wrap items-center justify-between gap-1.5">
                          <p className="text-xs font-semibold text-slate-800">What's left</p>
                          {sumTop3 > 0 && (
                            <p className="text-[11px] text-slate-600">
                              Top 3: +<span className="font-semibold text-blue-700">{sumTop3}%</span>
                            </p>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-1.5">
                          {topMissingExpanded.map((m) => (
                            <button
                              key={m.id}
                              onClick={() => setIsEditModalOpen(true)}
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs ring-1 transition ${m.required
                                ? "bg-red-50 text-red-700 ring-red-200 hover:bg-red-100"
                                : "bg-blue-50 text-blue-700 ring-blue-200 hover:bg-blue-100"
                                }`}
                            >
                              <span className="font-medium">{m.label}</span>
                              <span className="text-[10px] text-slate-500">+{m.contributionPercent}%</span>
                            </button>
                          ))}
                          {topMissingExpanded.length === 0 && (
                            <span className="text-xs text-emerald-700">All complete!</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3 mb-6">
                {/* Top profile area */}
                <div className="grid grid-cols-1 md:grid-cols-[60%_40%] gap-2">
                  {/* Left Section - Profile */}
                  <div className="relative">
                    {/* Cover Image */}
                    <div className="h-20 w-full bg-gradient-to-br from-[#1565C0] via-[#1E88E5] to-[#26C6DA] rounded-t-lg overflow-hidden">
                      <div className="flex absolute right-1 top-1 justify-end p-2 z-10">
                        <Button
                          onClick={() => setIsEditModalOpen(true)}
                          size="md"
                          bg={colors.fontBlue}
                          color="white"
                          _hover={{ bg: colors.textBlue }}
                          leftIcon={<Pen size={16} />}
                        >
                          Edit
                        </Button>
                      </div>
                    </div>

                    {/* Profile Section */}
                    <div className="rounded-b-lg bg-white">
                      <div className="px-4 py-2">
                        {/* Profile Picture */}
                        <div className="relative -mt-20 mb-4">
                          <div className="relative inline-block">
                            <div className="h-40 w-40 rounded-full border-2 border-gray-200 overflow-hidden bg-white">
                              {profileData.company_logo ? (
                                <Image
                                  src={`${base_url}${profileData.company_logo}`}
                                  alt={profileData.company_name}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <BuildingIcon className="h-full w-full p-6 text-gray-400" />
                              )}
                            </div>
                            <button
                              onClick={() => setIsLogoModalOpen(true)}
                              className="absolute bottom-2 right-2 bg-white text-blue-600 rounded-full w-9 h-9 flex items-center justify-center hover:bg-gray-50 border shadow-sm"
                              title="Change logo"
                            >
                              <Camera className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Profile Info */}
                        {(profileData.company_name || percentDisplay >= 0) && (
                          <div className="flex justify-between items-start flex-wrap gap-2">
                            <div className="flex-1 ">
                              <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                                {profileData.company_name || "Company"}
                              </h1>

                            </div>
                            {profileData.url && (
                              <a
                                className="inline-flex items-center text-blue-600 hover:underline text-sm"
                                href={profileData.url.startsWith("http") ? profileData.url : `https://${profileData.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View site <ExternalLink size={14} style={{ marginLeft: 6 }} />
                              </a>
                            )}
                          </div>
                        )}

                        {/* Contact Info */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 mb-2">
                          {/* Email */}
                          {profileData.email && (
                            <div className="flex items-center gap-2 text-gray-600 min-w-0">
                              <div className="flex-shrink-0">
                                <Mail className="w-4 h-4" color={colors.fontBlue} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm sm:text-base break-all min-w-0 truncate">{profileData.email}</span>
                                  {profileData.user?.email_verify === 0 ? (
                                    <Link to="/employer-dashboard/verify-email" className="flex-shrink-0">
                                      <span className="px-2 py-1 bg-green-500 text-white text-xs rounded cursor-pointer hover:bg-green-600 transition-colors duration-200 whitespace-nowrap">
                                        Verify?
                                      </span>
                                    </Link>
                                  ) : (
                                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Phone */}
                          {profileData.phone && (
                            <div className="flex items-center gap-2 text-gray-600 min-w-0">
                              <div className="flex-shrink-0">
                                <Phone className="w-4 h-4" color={colors.fontBlue} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm sm:text-base break-all min-w-0">{profileData.phone}</span>
                                  {profileData.user?.phone_verify === 0 ? (
                                    <Link to="/phone-verification-phone" className="flex-shrink-0">
                                      <span className="px-2 py-1 bg-green-500 text-white text-xs rounded cursor-pointer hover:bg-green-600 transition-colors duration-200 whitespace-nowrap">
                                        Verify?
                                      </span>
                                    </Link>
                                  ) : (
                                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Mobile */}
                          {(profileData.mobile || profileData.phone) && (
                            <div className="flex items-center gap-2 text-gray-600 min-w-0">
                              <div className="flex-shrink-0">
                                <PhoneArrowDownLeftIcon className="w-4 h-4" color={colors.fontBlue} />
                              </div>
                              <span className="text-sm sm:text-base break-all flex-1 min-w-0">
                                {profileData.mobile || "N/A"}
                              </span>
                            </div>
                          )}

                          {/* URL */}
                          {profileData.url && (
                            <div className="flex items-center gap-2 text-gray-600 min-w-0">
                              <div className="flex-shrink-0">
                                <Globe className="w-4 h-4" color={colors.fontBlue} />
                              </div>
                              <a
                                href={profileData.url.startsWith("http") ? profileData.url : `https://${profileData.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm sm:text-base break-all flex-1 min-w-0 hover:underline hover:text-blue-500 transition-colors duration-200"
                              >
                                {profileData.url}
                              </a>
                            </div>
                          )}

                          {/* Contact Person */}
                          {profileData.contact_person_name && (
                            <div className="flex items-center gap-2 text-gray-600 min-w-0">
                              <div className="flex-shrink-0">
                                <User className="w-4 h-4" color={colors.fontBlue} />
                              </div>
                              <span className="text-sm sm:text-base break-words flex-1 min-w-0 capitalize">
                                {profileData.contact_person_name}
                              </span>
                            </div>
                          )}

                          {/* Company Type */}
                          {profileData?.company_type && (
                            <div className="flex items-center gap-2 text-gray-600 min-w-0">
                              <div className="flex-shrink-0">
                                <Building2 className="w-4 h-4" color={colors.fontBlue} />
                              </div>
                              <span className="text-sm sm:text-base break-words flex-1 min-w-0 capitalize">
                                {profileData?.company_type}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Section - Call to Action Original Data if you want the subscription one try this */}
                  {/* <Box
                    p={4}
                    borderRadius="lg"
                    boxShadow="xl"
                    color="white"
                    className="bg-gradient-to-br from-[#1565C0] via-[#1E88E5] to-[#26C6DA]"
                    position="relative"
                    overflow="hidden"
                  >
                    <Box position="absolute" top="-20px" right="-20px" width="100px" height="100px" borderRadius="full" bg="whiteAlpha.100" />
                    <Box position="absolute" bottom="-10px" left="-10px" width="60px" height="60px" borderRadius="full" bg="whiteAlpha.50" />

                    <VStack align="stretch" spacing={3} position="relative" zIndex={1}>
                      <Box textAlign="center" className="flex justify-between flex-wrap gap-2">
                        <Flex align="start" justify="center">
                          <Box p={1.5} borderRadius="lg" mr={2}>
                            <CheckCircle2 className="w-5 h-5" />
                          </Box>
                          <VStack align="start" spacing={0}>
                            <Text className="text-left" fontSize="lg" fontWeight="bold">
                              {profileData.active_subscription?.subscriptions?.title || "No Current Plan"}
                              <Text fontSize="sm" opacity={0.9}>
                                {profileData.active_subscription?.subscriptions?.subscription_type || "—"} Subscription
                              </Text>
                            </Text>
                          </VStack>
                        </Flex>
                        <Box bg="whiteAlpha.200" px={2.5} py={1} borderRadius="full" display="inline-block">
                          <Text fontSize="xs" fontWeight="medium">
                            Valid until{" "}
                            {profileData.active_subscription?.validity
                              ? new Date(profileData.active_subscription.validity).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })
                              : "N/A"}
                          </Text>
                        </Box>
                      </Box>

                      <SimpleGrid columns={{ base: 2, sm: 3, md: 2 }} spacing={2}>
                        <Box
                          bg="whiteAlpha.150"
                          p={2.5}
                          borderRadius="lg"
                          backdropFilter="blur(10px)"
                          border="1px solid"
                          borderColor="whiteAlpha.200"
                          textAlign="center"
                          transition="all 0.3s"
                          _hover={{ transform: "translateY(-2px)", bg: "whiteAlpha.200" }}
                        >
                          <Flex justify="center" mb={1}>
                            <Box bg="whiteAlpha.300" p={1} borderRadius="full">
                              <Plus className="w-4 h-4" />
                            </Box>
                          </Flex>
                          <Text fontSize="lg" fontWeight="bold" mb={0.5}>
                            {formatNumber(profileData.active_subscription?.remaining_job_post || 0)}
                          </Text>
                          <Text fontSize="xs" opacity={0.9}>
                            Job Posts Left
                          </Text>
                        </Box>

                        <Box
                          bg="whiteAlpha.150"
                          p={2.5}
                          borderRadius="lg"
                          backdropFilter="blur(10px)"
                          border="1px solid"
                          borderColor="whiteAlpha.200"
                          textAlign="center"
                          transition="all 0.3s"
                          _hover={{ transform: "translateY(-2px)", bg: "whiteAlpha.200" }}
                        >
                          <Flex justify="center" mb={1}>
                            <Box bg="whiteAlpha.300" p={1} borderRadius="full">
                              <Users className="w-4 h-4" />
                            </Box>
                          </Flex>
                          <Text fontSize="lg" fontWeight="bold" mb={0.5}>
                            {formatNumber(profileData.active_subscription?.remaining_candidate_unlock || 0)}
                          </Text>
                          <Text fontSize="xs" opacity={0.9}>
                            Candidate Unlocks
                          </Text>
                        </Box>

                        <Box
                          bg="whiteAlpha.150"
                          p={2.5}
                          borderRadius="lg"
                          backdropFilter="blur(10px)"
                          border="1px solid"
                          borderColor="whiteAlpha.200"
                          textAlign="center"
                          transition="all 0.3s"
                          _hover={{ transform: "translateY(-2px)", bg: "whiteAlpha.200" }}
                        >
                          <Flex justify="center" mb={1}>
                            <Box bg="whiteAlpha.300" p={1} borderRadius="full">
                              <TrendingUp className="w-4 h-4" />
                            </Box>
                          </Flex>
                          <Text fontSize="lg" fontWeight="bold" mb={0.5}>
                            {formatNumber(profileData.active_subscription?.remaining_urgent_hiring_tag || 0)}
                          </Text>
                          <Text fontSize="xs" opacity={0.9}>
                            Urgent Tags Left
                          </Text>
                        </Box>

                        <Box
                          bg="whiteAlpha.150"
                          p={2.5}
                          borderRadius="lg"
                          backdropFilter="blur(10px)"
                          border="1px solid"
                          borderColor="whiteAlpha.200"
                          textAlign="center"
                          transition="all 0.3s"
                          _hover={{ transform: "translateY(-2px)", bg: "whiteAlpha.200" }}
                        >
                          <Flex justify="center" mb={1}>
                            <Box bg="whiteAlpha.300" p={1} borderRadius="full">
                              <FileText className="w-4 h-4" />
                            </Box>
                          </Flex>
                          <Text fontSize="lg" fontWeight="bold" mb={0.5}>
                            {formatNumber(profileData.active_subscription?.remaining_job_published || 0)}
                          </Text>
                          <Text fontSize="xs" opacity={0.9}>
                            Published Jobs Left
                          </Text>
                        </Box>
                      </SimpleGrid>

                      <VStack spacing={2}>
                        <ChakraTooltip label={disabledReason} isDisabled={!isDisabledjob}>
                          <span style={{ width: "100%" }}>
                            <Button
                              as={Link}
                              to="/employer-dashboard/post-job"
                              bg="white"
                              color={colors.fontBlue}
                              size="md"
                              width="full"
                              fontWeight="bold"
                              borderRadius="xl"
                              py={4}
                              isDisabled={isDisabledjob}
                              transition="all 0.3s"
                              leftIcon={<Plus className="w-4 h-4" />}
                            >
                              Post New Job
                            </Button>
                          </span>
                        </ChakraTooltip>
                      </VStack>
                    </VStack>
                  </Box> */}

                  {/* Right Section - Call to Action Dummy data */}
                  <Box
                    p={4}
                    borderRadius="lg"
                    boxShadow="xl"
                    color="white"
                    className="bg-gradient-to-br from-[#1565C0] via-[#1E88E5] to-[#26C6DA]"
                    position="relative"
                    overflow="hidden"
                  >
                    <Box position="absolute" top="-20px" right="-20px" width="100px" height="100px" borderRadius="full" bg="whiteAlpha.100" />
                    <Box position="absolute" bottom="-10px" left="-10px" width="60px" height="60px" borderRadius="full" bg="whiteAlpha.50" />

                    <VStack align="stretch" spacing={3} position="relative" zIndex={1}>

                      {/* Quick Actions Header */}
                      <Box textAlign="center" className="flex justify-between flex-wrap gap-2">
                        <Flex align="start" justify="center">
                          <Box p={1.5} borderRadius="lg" mr={2}>
                            <TrendingUp className="w-5 h-5" />
                          </Box>
                          <VStack align="start" spacing={0}>
                            <Text className="text-left" fontSize="lg" fontWeight="bold">
                              Quick Actions
                              <Text fontSize="sm" opacity={0.9}>
                                Manage your hiring activities
                              </Text>
                            </Text>
                          </VStack>
                        </Flex>
                        <Box bg="whiteAlpha.200" px={2.5} py={1} borderRadius="full" display="inline-block">
                          <Text fontSize="xs" fontWeight="medium">
                            {new Date().toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </Text>
                        </Box>
                      </Box>

                      {/* Stats Cards */}
                      <SimpleGrid columns={{ base: 2, sm: 3, md: 2 }} spacing={2}>
                        <Box
                          bg="whiteAlpha.150"
                          p={2.5}
                          borderRadius="lg"
                          backdropFilter="blur(10px)"
                          border="1px solid"
                          borderColor="whiteAlpha.200"
                          textAlign="center"
                          transition="all 0.3s"
                          _hover={{ transform: "translateY(-2px)", bg: "whiteAlpha.200" }}
                        >
                          <Flex justify="center" mb={1}>
                            <Box bg="whiteAlpha.300" p={1} borderRadius="full">
                              <Plus className="w-4 h-4" />
                            </Box>
                          </Flex>
                          <Text fontSize="lg" fontWeight="bold" mb={0.5}>
                            {formatNumber(profileData.active_subscription?.remaining_job_post || 0)}
                          </Text>
                          <Text fontSize="xs" opacity={0.9}>
                            Job Posts Left
                          </Text>
                        </Box>

                        <Box
                          bg="whiteAlpha.150"
                          p={2.5}
                          borderRadius="lg"
                          backdropFilter="blur(10px)"
                          border="1px solid"
                          borderColor="whiteAlpha.200"
                          textAlign="center"
                          transition="all 0.3s"
                          _hover={{ transform: "translateY(-2px)", bg: "whiteAlpha.200" }}
                        >
                          <Flex justify="center" mb={1}>
                            <Box bg="whiteAlpha.300" p={1} borderRadius="full">
                              <Users className="w-4 h-4" />
                            </Box>
                          </Flex>
                          <Text fontSize="lg" fontWeight="bold" mb={0.5}>
                            {formatNumber(profileData.active_subscription?.remaining_candidate_unlock || 0)}
                          </Text>
                          <Text fontSize="xs" opacity={0.9}>
                            Candidate Unlocks
                          </Text>
                        </Box>

                        <Box
                          bg="whiteAlpha.150"
                          p={2.5}
                          borderRadius="lg"
                          backdropFilter="blur(10px)"
                          border="1px solid"
                          borderColor="whiteAlpha.200"
                          textAlign="center"
                          transition="all 0.3s"
                          _hover={{ transform: "translateY(-2px)", bg: "whiteAlpha.200" }}
                        >
                          <Flex justify="center" mb={1}>
                            <Box bg="whiteAlpha.300" p={1} borderRadius="full">
                              <TrendingUp className="w-4 h-4" />
                            </Box>
                          </Flex>
                          <Text fontSize="lg" fontWeight="bold" mb={0.5}>
                            {formatNumber(profileData.active_subscription?.remaining_urgent_hiring_tag || 0)}
                          </Text>
                          <Text fontSize="xs" opacity={0.9}>
                            Urgent Tags Left
                          </Text>
                        </Box>

                        <Box
                          bg="whiteAlpha.150"
                          p={2.5}
                          borderRadius="lg"
                          backdropFilter="blur(10px)"
                          border="1px solid"
                          borderColor="whiteAlpha.200"
                          textAlign="center"
                          transition="all 0.3s"
                          _hover={{ transform: "translateY(-2px)", bg: "whiteAlpha.200" }}
                        >
                          <Flex justify="center" mb={1}>
                            <Box bg="whiteAlpha.300" p={1} borderRadius="full">
                              <FileText className="w-4 h-4" />
                            </Box>
                          </Flex>
                          <Text fontSize="lg" fontWeight="bold" mb={0.5}>
                            {formatNumber(profileData.active_subscription?.remaining_job_published || 0)}
                          </Text>
                          <Text fontSize="xs" opacity={0.9}>
                            Published Jobs Left
                          </Text>
                        </Box>
                      </SimpleGrid>

                      {/* Post Job Button */}
                      <VStack spacing={2}>
                        <ChakraTooltip label={disabledReason} isDisabled={!isDisabledjob}>
                          <span style={{ width: "100%" }}>
                            <Button
                              as={Link}
                              to="/employer-dashboard/post-job"
                              bg="white"
                              color={colors.fontBlue}
                              size="md"
                              width="full"
                              fontWeight="bold"
                              borderRadius="xl"
                              py={4}
                              isDisabled={isDisabledjob}
                              transition="all 0.3s"
                              _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                              leftIcon={<Plus className="w-4 h-4" />}
                            >
                              Post New Job
                            </Button>
                          </span>
                        </ChakraTooltip>
                      </VStack>
                    </VStack>
                  </Box>
                </div>

                {/* Job Statistics */}
                <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
                  <Text fontSize="lg" fontWeight="semibold" mb={6} color={colors.LgColor} textAlign="center">
                    Job Statistics Analytics
                  </Text>
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                    {/* Overview Bar Chart */}
                    <Box bg={colors.lightGray} p={2} borderRadius="lg">
                      <Text fontSize="md" fontWeight="semibold" mb={4} color={colors.LgColor} textAlign="center">
                        Job Overview
                      </Text>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={overviewData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 12, fill: colors.darkGray }} axisLine={{ stroke: colors.lightGray }} />
                          <YAxis tick={{ fontSize: 12, fill: colors.darkGray }} axisLine={{ stroke: colors.lightGray }} />
                          <RechartsTooltip
                            contentStyle={{
                              backgroundColor: "white",
                              border: `1px solid ${colors.lightGray}`,
                              borderRadius: "8px",
                              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                            }}
                          />
                          <Bar dataKey="value" fill={colors.fontBlue} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>

                    {/* Application Status Pie Chart */}
                    <Box bg={colors.lightGray} p={2} borderRadius="lg">
                      <Text fontSize="md" fontWeight="semibold" mb={4} color={colors.LgColor} textAlign="center">
                        Application Status Distribution
                      </Text>
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={statusData}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {statusData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            contentStyle={{
                              backgroundColor: "white",
                              border: `1px solid ${colors.lightGray}`,
                              borderRadius: "8px",
                              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                            }}
                          />
                          <Legend wrapperStyle={{ fontSize: "12px", color: colors.darkGray }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </SimpleGrid>

                  {/* Summary Cards */}
                  <SimpleGrid columns={{ base: 2, sm: 3, md: 5 }} spacing={3} mt={6}>
                    <Box bg="white" p={3} borderRadius="md" border={`1px solid ${colors.lightGray}`} textAlign="center">
                      <Flex justifyContent="center" alignItems="center" mb={1.5}>
                        <TrendingUp size={16} color={colors.fontBlue} />
                      </Flex>
                      <Text fontSize="lg" fontWeight="bold" color={colors.textBlue}>
                        {formatNumber(profileData.total_job_count || 0)}
                      </Text>
                      <Text fontSize="xs" color={colors.darkGray}>Total Jobs</Text>
                    </Box>
                    <Box bg="white" p={3} borderRadius="md" border={`1px solid ${colors.lightGray}`} textAlign="center">
                      <Flex justifyContent="center" alignItems="center" mb={1.5}>
                        <Users size={16} color={colors.fontBlue} />
                      </Flex>
                      <Text fontSize="lg" fontWeight="bold" color={colors.textBlue}>
                        {formatNumber(profileData.total_applicant_count || 0)}
                      </Text>
                      <Text fontSize="xs" color={colors.darkGray}>Applicants</Text>
                    </Box>
                    <Box bg="white" p={3} borderRadius="md" border={`1px solid ${colors.lightGray}`} textAlign="center">
                      <Flex justifyContent="center" alignItems="center" mb={1.5}>
                        <FileText size={16} color={colors.fontBlue} />
                      </Flex>
                      <Text fontSize="lg" fontWeight="bold" color={colors.textBlue}>
                        {formatNumber(profileData.total_published_job_count || 0)}
                      </Text>
                      <Text fontSize="xs" color={colors.darkGray}>Published</Text>
                    </Box>
                    <Box bg="white" p={3} borderRadius="md" border={`1px solid ${colors.lightGray}`} textAlign="center">
                      <Flex justifyContent="center" alignItems="center" mb={1.5}>
                        <Send size={16} color="#10B981" />
                      </Flex>
                      <Text fontSize="lg" fontWeight="bold" color="#10B981">
                        {formatNumber(profileData.status_counts?.applied || 0)}
                      </Text>
                      <Text fontSize="xs" color={colors.darkGray}>Applied</Text>
                    </Box>
                    <Box bg="white" p={3} borderRadius="md" border={`1px solid ${colors.lightGray}`} textAlign="center">
                      <Flex justifyContent="center" alignItems="center" mb={1.5}>
                        <UserCheck size={16} color="#10B981" />
                      </Flex>
                      <Text fontSize="lg" fontWeight="bold" color="#10B981">
                        {formatNumber(profileData.status_counts?.shortlist || 0)}
                      </Text>
                      <Text fontSize="xs" color={colors.darkGray}>Shortlisted</Text>
                    </Box>
                    <Box bg="white" p={3} borderRadius="md" border={`1px solid ${colors.lightGray}`} textAlign="center">
                      <Flex justifyContent="center" alignItems="center" mb={1.5}>
                        <Calendar size={16} color="#F59E0B" />
                      </Flex>
                      <Text fontSize="lg" fontWeight="bold" color="#F59E0B">
                        {formatNumber(profileData.status_counts?.interview_scheduled || 0)}
                      </Text>
                      <Text fontSize="xs" color={colors.darkGray}>Interviews</Text>
                    </Box>
                    <Box bg="white" p={3} borderRadius="md" border={`1px solid ${colors.lightGray}`} textAlign="center">
                      <Flex justifyContent="center" alignItems="center" mb={1.5}>
                        <UserMinusIcon size={16} color="#f52e0b" />
                      </Flex>
                      <Text fontSize="lg" fontWeight="bold" color="#f52e0b">
                        {formatNumber(profileData.status_counts?.rejected || 0)}
                      </Text>
                      <Text fontSize="xs" color={colors.darkGray}>Rejected</Text>
                    </Box>
                    <Box bg="white" p={3} borderRadius="md" border={`1px solid ${colors.lightGray}`} textAlign="center">
                      <Flex justifyContent="center" alignItems="center" mb={1.5}>
                        <PauseCircleIcon size={16} color="#f5c518" />
                      </Flex>
                      <Text fontSize="lg" fontWeight="bold" color="#f5c518">
                        {formatNumber(profileData.status_counts?.on_hold || 0)}
                      </Text>
                      <Text fontSize="xs" color={colors.darkGray}>On Hold</Text>
                    </Box>
                    <Box bg="white" p={3} borderRadius="md" border={`1px solid ${colors.lightGray}`} textAlign="center">
                      <Flex justifyContent="center" alignItems="center" mb={1.5}>
                        <UserPlusIcon size={16} color="#a3d9a5" />
                      </Flex>
                      <Text fontSize="lg" fontWeight="bold" color="#a3d9a5">
                        {formatNumber(profileData.status_counts?.hired || 0)}
                      </Text>
                      <Text fontSize="xs" color={colors.darkGray}>Hired</Text>
                    </Box>
                    <Box bg="white" p={3} borderRadius="md" border={`1px solid ${colors.lightGray}`} textAlign="center">
                      <Flex justifyContent="center" alignItems="center" mb={1.5}>
                        <MessageCircleIcon size={16} color="#5bc0de" />
                      </Flex>
                      <Text fontSize="lg" fontWeight="bold" color="#5bc0de">
                        {formatNumber(profileData.status_counts?.contacted || 0)}
                      </Text>
                      <Text fontSize="xs" color={colors.darkGray}>Contacted</Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Tabs>
                  <TabList>
                    <Tab>About</Tab>
                    <Tab>Media</Tab>
                    <Tab>Company Description</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel>
                      <div className="bg-white rounded-xl p-6 shadow-md mt-4">
                        <div className="mb-2 flex items-center justify-between">
                          <h2 className="text-2xl font-semibold text-gray-700">About Company</h2>
                          <ChakraTooltip label="Edit company profile">
                            <Button size="sm" variant="outline" leftIcon={<Pen size={14} />} onClick={() => setIsEditModalOpen(true)}>
                              Edit
                            </Button>
                          </ChakraTooltip>
                        </div>
                        <div>
                          <CompanyAbout
                            title={<span className="flex items-center"><Building2 className={`${iconColors.companyType} w-4 h-4 mr-2`} />Company Type</span>}
                            name={profileData.company_type}
                          />
                          <CompanyAbout
                            title={<span className="flex items-center"><Factory className={`${iconColors.industryType} w-4 h-4 mr-2`} />Industry Type</span>}
                            name={profileData.industry_type}
                          />
                          <CompanyAbout
                            title={<span className="flex items-center"><User className={`${iconColors.contactPerson} w-4 h-4 mr-2`} />Contact Person</span>}
                            name={profileData.contact_person_name}
                          />
                          <CompanyAbout
                            title={<span className="flex items-center"><Globe className={`${iconColors.website} w-4 h-4 mr-2`} />Website URL</span>}
                            name={profileData.url}
                          />
                          <CompanyAbout
                            title={<span className="flex items-center"><Phone className={`${iconColors.phone} w-4 h-4 mr-2`} />Phone Number</span>}
                            name={profileData.phone}
                          />
                          <CompanyAbout
                            title={<span className="flex items-center"><FileText className={`${iconColors.tanNumber} w-4 h-4 mr-2`} />TAN Number</span>}
                            name={profileData.tan_number}
                          />
                          <CompanyAbout
                            title={<span className="flex items-center"><FileText className={`${iconColors.gstNumber} w-4 h-4 mr-2`} />GST Number</span>}
                            name={profileData.gst_number}
                          />
                          <CompanyAbout
                            title={<span className="flex items-center"><FileText className={`${iconColors.panNumber} w-4 h-4 mr-2`} />PAN Number</span>}
                            name={profileData.pan_number}
                          />
                          <CompanyAbout
                            title={<span className="flex items-center"><FileText className={`${iconColors.nameOnPAN} w-4 h-4 mr-2`} />Name on PAN Card</span>}
                            name={profileData.name_as_pan_number}
                          />
                          <CompanyAbout
                            title={<span className="flex items-center"><MapPin className={`${iconColors.address} w-4 h-4 mr-2`} />Address on PAN Card</span>}
                            name={profileData.address1}
                          />
                        </div>
                      </div>
                    </TabPanel>
                    <TabPanel>
                      <div className="bg-white rounded-xl p-6 shadow-md mt-4">
                        <div className="mb-2">
                          <EmployerMedia uuid={profileData.uuid} />
                        </div>
                      </div>
                    </TabPanel>
                    <TabPanel>
                      <div className="bg-white rounded-xl p-6 shadow-md mt-4">
                        <div className="mb-2">
                          <h2 className="text-2xl font-semibold text-gray-700">About Company</h2>
                          <Text color="gray.600" mt={2}>
                            {profileData?.company_description || "No description provided yet."}
                          </Text>
                        </div>
                      </div>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </div>

              <EditModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                profileData={profileData}
                onUpdate={handleUpdateProfile}
              />

              <Modal isOpen={isLogoModalOpen} onClose={() => setIsLogoModalOpen(false)} isCentered>
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader>Upload Company Logo</ModalHeader>
                  <ModalBody>
                    <FormControl>
                      <FormLabel htmlFor="logo">Select Logo</FormLabel>
                      <Box
                        border="2px dashed"
                        borderColor="gray.200"
                        borderRadius="md"
                        p={4}
                        textAlign="center"
                        onDragOver={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                        onDrop={onDropLogo}
                        cursor="pointer"
                        onClick={() => document.getElementById("logo-input")?.click()}
                      >
                        <UploadCloud style={{ display: "inline", marginRight: 8 }} size={18} />
                        Drag & drop or click to select an image (max 2MB)
                      </Box>
                      <Input
                        type="file"
                        id="logo-input"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e.target.files?.[0])}
                        display="none"
                      />
                      {previewUrl && (
                        <Image
                          src={previewUrl || "/placeholder.svg"}
                          alt="Logo Preview"
                          boxSize="200px"
                          objectFit="cover"
                          mt={4}
                          borderRadius="md"
                          border="1px solid #E5E7EB"
                        />
                      )}
                    </FormControl>
                  </ModalBody>
                  <ModalFooter>
                    <Button variant="ghost" onClick={() => setIsLogoModalOpen(false)} isDisabled={logoSaving}>
                      Cancel
                    </Button>
                    <Button colorScheme="blue" onClick={saveLogo} ml={3} isLoading={logoSaving} loadingText="Saving...">
                      Save
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            </div>
          ) : (
            <div className="min-h-screen">
              <CompanyProfileSkeleton />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}